import React, { useMemo } from 'react';

import classNames from 'classnames';
import debounce from 'lodash/debounce';
import ReactSelect, { components as reactSelectComponents } from 'react-select';
import ReactSelectAsync from 'react-select/async';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/pro-solid-svg-icons';

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import type { ValueContainerProps } from 'react-select';

import './styles.scss';

export { components } from 'react-select';

type MessageFn = (obj: { inputValue: string }) => React.ReactNode;
type MessageProp = React.ReactNode | MessageFn;
type ReactSelectProps<Option> = React.ComponentProps<typeof ReactSelect<Option>>;
type ReactSelectAsyncProps<Option> = React.ComponentProps<typeof ReactSelectAsync<Option>>;

type BaseProps = {
  alignRight?: boolean;
  icon?: IconDefinition | boolean;
  noOptionsMessage?: MessageProp;
};

type SyncProps<Option> = BaseProps & ReactSelectProps<Option> & {
  debounce?: null;
  loadOptions?: null;
  loadingMessage?: null;
}

type AsyncProps<Option> = BaseProps & Omit<ReactSelectAsyncProps<Option>, 'noOptionsMessage' | 'loadingMessage'> & {
  debounce?: boolean | number | null;
  loadingMessage?: MessageProp;
};

type Props<Option> = SyncProps<Option> | AsyncProps<Option>;

// A wrapper around react-select's Select/AsyncSelect with Blueprint styling
export function Select<Option = unknown>({
  alignRight,
  components,
  debounce: debounceProp = true,
  icon,
  loadingMessage = 'Searching...',
  loadOptions: loadOptionsProp,
  noOptionsMessage = 'No results',
  ...props
}: Props<Option>) {
  const isAsync = loadOptionsProp != null;
  const Component = isAsync ? ReactSelectAsync : ReactSelect;

  if (icon === true || (isAsync && icon == null)) {
    icon = faSearch;
  }

  const loadingMessageWrapper = useMemo(() => {
    return typeof loadingMessage === 'function'
      ? loadingMessage
      : () => loadingMessage;
  }, [loadingMessage]);

  const noOptionsMessageWrapper = useMemo(() => {
    return typeof noOptionsMessage === 'function'
      ? noOptionsMessage
      : ({ inputValue }: { inputValue: string }) => {
        // Don't show noOptionsMessage until the user starts typing
        return inputValue ? noOptionsMessage : null;
      };
  }, [noOptionsMessage]);

  const loadOptions = useMemo(() => {
    if (!debounceProp || !loadOptionsProp) return loadOptionsProp;

    const debounced = debounce((input, callback) => {
      const result = loadOptionsProp(input, callback);
      if (result && typeof result.then === 'function' && callback) {
        result.then(callback).catch(() => callback([]));
      }
    }, debounceProp === true ? 300 : debounceProp);

    return debounced;
  }, [debounceProp, loadOptionsProp]);

  return (
    <Component
      {...props}
      classNames={{
        control: state => classNames('react-select form-select py-0', {
          'react-select--disabled': state.isDisabled,
          'react-select--focus': state.isFocused,
        }),
        indicatorsContainer: () => 'text-muted',
        menu: () => classNames('react-select__menu dropdown-menu show', {
          'react-select__menu--right': alignRight,
        }),
        multiValue: () => 'bg-dark-subtle px-2 py-0-5 rounded',
        option: state => classNames('react-select__option dropdown-item', {
          active: state.isSelected,
        }),
        placeholder: () => 'text-muted',
        valueContainer: () => classNames('gap-2', { 'ps-6': icon }),
      }}
      components={{
        DropdownIndicator: null,
        ValueContainer: icon ? ValueContainerWithIcon : reactSelectComponents.ValueContainer,
        ...components,
      }}
      loadingMessage={loadingMessageWrapper}
      loadOptions={loadOptions}
      noOptionsMessage={noOptionsMessageWrapper}
      unstyled

      // icon is a custom prop for ValueContainerWithIcon
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      {...{ icon } as any}
    />
  );
}

interface ValueContainerWithIconProps extends ValueContainerProps {
  selectProps: ValueContainerProps['selectProps'] & {
    icon: IconDefinition;
  };
}

function ValueContainerWithIcon({ children, selectProps, ...props }: ValueContainerWithIconProps) {
  return (
    <reactSelectComponents.ValueContainer selectProps={selectProps} {...props}>
      <FontAwesomeIcon className="position-absolute" icon={selectProps.icon} />
      {children}
    </reactSelectComponents.ValueContainer>
  );
}
